import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  ShoppingBag,
  Package,
  Clock,
  Plus,
  ChevronRight,
  CreditCard,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { useBusinessStore } from "@/stores/businessStore";
import type { OrderStatus, Product, Order } from "@/stores/businessStore";
import { useLanguageStore } from "@/stores/languageStore";
import { BusinessOnboarding } from "./BusinessOnboarding";
import { PaymentsTab } from "./PaymentsTab";
import { cn } from "@/lib/cn";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const staggerStats = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
};

type TabKey = "products" | "orders" | "payments";

const TABS: { key: TabKey; label: string; labelAm: string; icon: LucideIcon }[] = [
  { key: "products", label: "Products", labelAm: "ምርቶች", icon: Package },
  { key: "orders", label: "Orders", labelAm: "ትዕዛዞች", icon: ShoppingBag },
  { key: "payments", label: "Payments", labelAm: "ክፍያዎች", icon: CreditCard },
];

function formatETB(amount: number): string {
  return `${amount.toLocaleString("en-US")} ETB`;
}

function relativeTime(dateString: string): string {
  const seconds = Math.floor(
    (Date.now() - new Date(dateString).getTime()) / 1000,
  );
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

function SectionHeader({ label, labelAm }: { label: string; labelAm: string }) {
  const language = useLanguageStore((state) => state.language);

  return (
    <div className="mb-3">
      <h2 className="font-headline text-base font-bold tracking-tight text-[var(--color-text)]">
        {localize(label, labelAm, language)}
      </h2>
    </div>
  );
}

function TabBar({
  activeTab,
  onTabChange,
}: {
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
}) {
  const language = useLanguageStore((state) => state.language);

  return (
    <div className="mb-4 flex gap-1 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-1">
      {TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onTabChange(tab.key)}
          className={cn(
            "relative flex flex-1 items-center justify-center gap-1.5 rounded-[var(--radius-md)] px-2 py-2 text-xs font-medium transition-colors min-h-[44px]",
            activeTab === tab.key
              ? "bg-[var(--color-surface)] text-[var(--color-text)] shadow-sm"
              : "text-[var(--color-text-muted)]",
          )}
        >
          <tab.icon className="h-3.5 w-3.5" />
          {language === "am" ? tab.labelAm : tab.label}
          {activeTab === tab.key && (
            <motion.div
              layoutId="business-tab-indicator"
              className="absolute inset-x-1 -bottom-0.5 h-0.5 rounded-full bg-[var(--color-primary)]"
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          )}
        </button>
      ))}
    </div>
  );
}

interface MiniStatProps {
  icon: LucideIcon;
  label: string;
  labelAm: string;
  value: string;
  accentBg: string;
  accentText: string;
}

function MiniStat({ icon: Icon, label, labelAm, value, accentBg, accentText }: MiniStatProps) {
  const language = useLanguageStore((state) => state.language);

  return (
    <motion.div
      variants={fadeUp}
      className="flex flex-col gap-1.5 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-3 shadow-sm"
    >
      <div
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]",
          accentBg,
        )}
      >
        <Icon className={cn("h-4 w-4", accentText)} />
      </div>
      <span className="font-headline text-lg font-bold text-[var(--color-text)]">
        {value}
      </span>
      <div className="flex flex-col">
        <span className="text-xs font-medium text-[var(--color-text-secondary)]">
          {localize(label, labelAm, language)}
        </span>
      </div>
    </motion.div>
  );
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: "bg-amber-50 text-amber-700",
  confirmed: "bg-blue-50 text-blue-700",
  processing: "bg-indigo-50 text-indigo-700",
  shipped: "bg-purple-50 text-purple-700",
  delivered: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <span
      className={cn(
        "rounded-[var(--radius-full)] px-2 py-0.5 text-xs font-medium capitalize",
        STATUS_COLORS[status],
      )}
    >
      {status}
    </span>
  );
}

function ProductCard({ product }: { product: Product }) {
  return (
    <motion.div
      variants={fadeUp}
      className="flex w-36 shrink-0 flex-col rounded-[var(--radius-lg)] bg-[var(--color-surface)]
                 p-3 shadow-sm transition-transform active:scale-[0.97]"
    >
      <div className="mb-2 flex h-20 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-bg)]">
        <Package className="h-8 w-8 text-[var(--color-text-muted)]" />
      </div>
      <span className="mb-0.5 truncate text-sm font-semibold text-[var(--color-text)]">
        {product.name}
      </span>
      <span className="text-[10px] text-[var(--color-text-muted)]">
        {product.nameAmharic}
      </span>
      <span className="mb-2 mt-1 font-headline text-sm font-bold text-[var(--color-text)]">
        {formatETB(product.price)}
      </span>
      <span
        className={cn(
          "self-start rounded-[var(--radius-full)] px-2 py-0.5 text-xs font-medium",
          product.inStock
            ? "bg-emerald-50 text-emerald-700"
            : "bg-red-50 text-red-700",
        )}
      >
        {product.inStock ? "In Stock" : "Out of Stock"}
      </span>
    </motion.div>
  );
}

function OrderItem({ order }: { order: Order }) {
  function navigateToOrder() {
    window.location.hash = `/business/orders/${order.id}`;
  }

  return (
    <motion.button
      type="button"
      onClick={navigateToOrder}
      variants={fadeUp}
      className="flex w-full items-center gap-3 rounded-[var(--radius-lg)] bg-[var(--color-surface)]
                 p-3 text-left shadow-sm transition-transform active:scale-[0.98] min-h-[44px]"
    >
      <div className="flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-sm font-semibold text-[var(--color-text)]">
            {order.contactName}
          </span>
          <StatusBadge status={order.status} />
        </div>
        <p className="text-xs text-[var(--color-text-secondary)]">
          {order.productName} x{order.quantity} ={" "}
          <span className="font-bold">{formatETB(order.totalPrice)}</span>
        </p>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          {relativeTime(order.createdAt)}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
    </motion.button>
  );
}

function ProductsTab({ products }: { products: Product[] }) {
  function navigateToAddProduct() {
    window.location.hash = "/business/add-product";
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <SectionHeader label="Your Products" labelAm="ምርቶችዎ" />
        <button
          type="button"
          onClick={navigateToAddProduct}
          className="flex items-center gap-1 rounded-[var(--radius-md)] bg-[var(--color-primary)]
                     px-3 py-1.5 text-xs font-medium text-white min-h-[44px]
                     transition-colors active:scale-[0.95]"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>

      {products.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          No products yet. Add your first product to get started.
        </p>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
        >
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </motion.div>
      )}
    </section>
  );
}

function OrdersTab({ orders }: { orders: Order[] }) {
  const sortedOrders = [...orders].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <section>
      <SectionHeader label="Recent Orders" labelAm="የቅርብ ጊዜ ትዕዛዞች" />

      {sortedOrders.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          No orders yet. Orders will appear here when customers place them.
        </p>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="flex flex-col gap-2"
        >
          {sortedOrders.map((order) => (
            <OrderItem key={order.id} order={order} />
          ))}
        </motion.div>
      )}
    </section>
  );
}

export default function BusinessPage() {
  const { products, orders, stats, isLoading, fetchProducts, fetchOrders } =
    useBusinessStore();
  const [activeTab, setActiveTab] = useState<TabKey>("products");

  useEffect(() => {
    fetchProducts();
    fetchOrders();
  }, [fetchProducts, fetchOrders]);

  if (isLoading && products.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Business" />
        <LoadingSkeleton variant="card" count={4} className="px-4 pt-4" />
      </div>
    );
  }

  if (products.length === 0 && orders.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Business" />
        <div className="flex-1 px-4 pb-24 pt-2">
          <BusinessOnboarding />
        </div>
      </div>
    );
  }

  const statCards: MiniStatProps[] = [
    {
      icon: TrendingUp,
      label: "Revenue",
      labelAm: "ገቢ",
      value: formatETB(stats.totalRevenue),
      accentBg: "bg-emerald-50",
      accentText: "text-emerald-600",
    },
    {
      icon: ShoppingBag,
      label: "Orders",
      labelAm: "ትዕዛዞች",
      value: String(stats.totalOrders),
      accentBg: "bg-blue-50",
      accentText: "text-blue-600",
    },
    {
      icon: Package,
      label: "Products",
      labelAm: "ምርቶች",
      value: String(stats.totalProducts),
      accentBg: "bg-purple-50",
      accentText: "text-purple-600",
    },
    {
      icon: Clock,
      label: "Pending",
      labelAm: "በመጠባበቅ ላይ",
      value: String(stats.pendingOrders),
      accentBg: "bg-red-50",
      accentText: "text-red-600",
    },
  ];

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Business" />

      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="flex-1 px-4 pb-24 pt-2"
      >
        <motion.div
          variants={staggerStats}
          initial="hidden"
          animate="visible"
          className="mb-6 grid grid-cols-2 gap-3"
        >
          {statCards.map((stat) => (
            <MiniStat key={stat.label} {...stat} />
          ))}
        </motion.div>

        <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === "products" && <ProductsTab products={products} />}
        {activeTab === "orders" && <OrdersTab orders={orders} />}
        {activeTab === "payments" && <PaymentsTab orders={orders} />}
      </motion.main>
    </div>
  );
}
